export function initKendoGridAutoSize() {
  
  const COLUMN_WIDTHS = {
    checkbox: 80,
    action: 60,
    type: 100,
    id: 120,
    uuid: 300,
    date: 120,
    text: 150,
    textMin: 100,
  };

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const getColumnType = (title, hasCheckbox) => {
    if (hasCheckbox) return 'checkbox';
    if (title === '' || title === ' ') return 'action';
    if (title === 'type') return 'type';
    if ((title.includes(' id') || title.endsWith('id')) && !title.includes('description')) return 'id';
    if (title.includes('date') || title.includes('creation') || title.includes('time')) return 'date';
    return 'text';
  };

  const getColumnWidth = (colType, isMobile) => {
    const mobileWidths = { checkbox: 50, action: 50, type: 80, id: 100, uuid: 200, date: 100 };
    if (colType === 'text') return null;
    return isMobile ? mobileWidths[colType] : COLUMN_WIDTHS[colType];
  };

  const analyzeColumn = (headerCell, bodyCell, isMobile) => {
    if (!bodyCell) {
      return { type: 'unknown', width: null, flexible: false, minWidth: COLUMN_WIDTHS.textMin };
    }

    const hasCheckbox = bodyCell.querySelector('input[type="checkbox"]');
    const title = headerCell.textContent?.toLowerCase().trim() || '';
    let colType = getColumnType(title, hasCheckbox);

    // Promote plain 'id' columns to 'uuid' if the cell value looks like a UUID
    if (colType === 'id') {
      const cellText = bodyCell.textContent?.trim() || '';
      if (UUID_REGEX.test(cellText)) {
        colType = 'uuid';
      }
    }

    const width = getColumnWidth(colType, isMobile);
    const flexible = colType === 'text';
    const minWidth = isMobile ? COLUMN_WIDTHS.textMin : COLUMN_WIDTHS.text;

    return { type: colType, width, flexible, minWidth };
  };

  const calculateColumnWidth = (info, flexibleWidth, needsScroll) => {
    if (!info.flexible) {
      return info.width;
    }
    if (needsScroll) {
      return info.minWidth;
    }
    return Math.max(flexibleWidth, info.minWidth);
  };

  const applyColumnWidths = (columnInfo, headerCols, contentCols, flexibleWidth, needsScroll) => {
    columnInfo.forEach((info, index) => {
      const headerCol = headerCols[index];
      const contentCol = contentCols[index];

      if (!headerCol || !contentCol) return;

      const width = calculateColumnWidth(info, flexibleWidth, needsScroll);

      if (width) {
        headerCol.style.width = `${width}px`;
        contentCol.style.width = `${width}px`;
      }
    });
  };

  const enableHorizontalScroll = (grid, headerTable, contentTable, totalMinWidth) => {
    const headerWrap = grid.querySelector('.k-grid-header-wrap');
    const contentWrap = grid.querySelector('.k-grid-content');
    
    if (headerWrap) headerWrap.style.overflowX = 'auto';
    if (contentWrap) contentWrap.style.overflowX = 'auto';

    const minTableWidth = totalMinWidth + 20;
    headerTable.style.minWidth = `${minTableWidth}px`;
    contentTable.style.minWidth = `${minTableWidth}px`;
  };

  const processGrid = (grid) => {
    if (grid.dataset.autoSized) return;

    const headerTable = grid.querySelector('.k-grid-header table');
    const contentTable = grid.querySelector('.k-grid-content table');
    if (!headerTable || !contentTable) return;

    const headerColGroup = headerTable.querySelector('colgroup');
    const contentColGroup = contentTable.querySelector('colgroup');
    if (!headerColGroup || !contentColGroup) return;

    const headerCols = headerColGroup.querySelectorAll('col');
    const contentCols = contentColGroup.querySelectorAll('col');
    const headerCells = headerTable.querySelectorAll('th');

    const contentRows = contentTable.querySelectorAll('tbody tr');
    if (contentRows.length === 0) return;

    const bodyCells = contentRows[0]?.querySelectorAll('td');
    if (!bodyCells || bodyCells.length === 0) return;

    const gridWidth = grid.offsetWidth || 800;
    const isMobile = gridWidth < 600;

    let totalFixedWidth = 0;
    let flexibleCount = 0;

    const columnInfo = Array.from(headerCells).map((headerCell, index) => {
      const info = analyzeColumn(headerCell, bodyCells[index], isMobile);
      if (info.flexible) flexibleCount++;
      else if (info.width) totalFixedWidth += info.width;
      return info;
    });

    const availableWidth = gridWidth - totalFixedWidth - 20;
    const flexibleWidth = flexibleCount > 0 
      ? Math.max(COLUMN_WIDTHS.textMin, Math.floor(availableWidth / flexibleCount))
      : COLUMN_WIDTHS.text;

    const totalMinWidth = totalFixedWidth + (flexibleCount * COLUMN_WIDTHS.textMin);
    const needsScroll = totalMinWidth > gridWidth;

    applyColumnWidths(columnInfo, headerCols, contentCols, flexibleWidth, needsScroll);

    if (needsScroll) {
      enableHorizontalScroll(grid, headerTable, contentTable, totalMinWidth);
    }

    grid.dataset.autoSized = 'true';
  };

  const processAllGrids = () => {
    document.querySelectorAll('.k-grid').forEach(processGrid);
  };

  let resizeTimeout;
  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      document.querySelectorAll('.k-grid[data-auto-sized]').forEach(grid => {
        grid.dataset.autoSized = '';
      });
      processAllGrids();
    }, 250);
  };

  window.addEventListener('resize', handleResize);
  setTimeout(processAllGrids, 100);

  const shouldProcessMutation = (mutation) => {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList?.contains('k-grid') || node.querySelector?.('.k-grid')) {
          return true;
        }
      }
    }

    const gridContent = mutation.target.closest?.('.k-grid-content');
    if (gridContent) {
      const grid = gridContent.closest('.k-grid');
      if (grid?.dataset.autoSized) {
        grid.dataset.autoSized = '';
        return true;
      }
    }
    return false;
  };

  const observer = new MutationObserver((mutations) => {
    const shouldProcess = mutations.some(shouldProcessMutation);

    if (shouldProcess) {
      clearTimeout(observer.timeout);
      observer.timeout = setTimeout(processAllGrids, 150);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  return () => {
    observer.disconnect();
    window.removeEventListener('resize', handleResize);
  };
}